import {
  mdiAirConditioner,
  mdiCeilingFan,
  mdiCctv,
  mdiCurtains,
  mdiDiscPlayer,
  mdiDoorbell,
  mdiDoorbellVideo,
  mdiDoorClosedLock,
  mdiFan,
  mdiFire,
  mdiFireAlert,
  mdiGate,
  mdiLedStripVariant,
  mdiLightbulbVariant,
  mdiLightbulbVariantOutline,
  mdiLightningBolt,
  mdiLock,
  mdiLockOpen,
  mdiMonitor,
  mdiMotionSensor,
  mdiMotionSensorOff,
  mdiPower,
  mdiPowerSocketUs,
  mdiPowerStandby,
  mdiProjector,
  mdiPump,
  mdiRemoteTv,
  mdiRobotVacuum,
  mdiSetTopBox,
  mdiShieldHome,
  mdiSmokeDetector,
  mdiSmokeDetectorOff,
  mdiSpeaker,
  mdiWater,
} from "@mdi/js";
import { DEVICE_TYPES as T, GATE_TYPES, REMOTE_TYPES as R } from "../../../constants/devices";

// Web port of the mobile app's DeviceSpecific.getSwitchColor and getSwitchIcon
// (app/theme/DeviceSpecific.js). Both take a device type (any case) or an IR
// remote type. Keep them in step with the app so cards look the same.

const key = (type) => (type ?? "").toUpperCase();

// Gradient [top, bottom] a card or detail page fills with when active.
export const deviceColors = (type, active) => {
  const t = key(type);
  if (GATE_TYPES.includes(t)) return ["#B2AA95", "#B2AA95"];
  switch (t) {
    case T.LIGHT:
      return ["rgba(252, 224, 84, 1)", "rgba(255, 204, 59, 1)"];
    case T.SOCKET:
      return ["rgba(51, 85, 255, 1)", "rgba(63, 92, 238, 1)"];
    case T.FAN:
      return ["rgba(90, 33, 232, 1)", "rgba(90, 33, 232, 1)"];
    case T.CURTAIN_MODULE:
      return ["#4B5234", "#4B5234"];
    case T.DIMMER_MODULE:
      return ["rgba(249, 229, 123, 1)", "rgba(255, 204, 59, 1)"];
    case T.DW_SENSOR:
      return active ? ["#00F260", "#0575E6"] : ["#0575E6", "red"];
    case T.RGB_LIGHT:
      return ["#0a62f1", "green"];
    case T.PIR_SENSOR:
    case T.GAS_SENSOR:
    case T.SMOKE_DETECTOR:
      return active ? ["#8d48fe", "#0575E6"] : ["#0575E6", "red"];
    case T.ROBOT_VACUUM_CLEANER:
      return ["#5F6162", "#5F6162"];
    case T.SECURITY_ALARM:
      return active ? ["#C40104", "#570203"] : ["#FF0003", "#4B0102"];
    case T.ENERGY_METER:
      return ["#DFE7FF", "#DFE7FF"];
    default:
      return ["rgba(224, 0, 0, 1)", "rgba(224, 0, 0, 1)"];
  }
};

// Colour of a device's usage history timeline (the second gradient colour).
export const historyColor = (type) => {
  const t = key(type);
  if ([T.DW_SENSOR, T.PIR_SENSOR, T.GAS_SENSOR, T.SMOKE_DETECTOR].includes(t)) return "red";
  if (t === T.SECURITY_ALARM) return "#4B0102";
  return deviceColors(t, false)[1];
};

export const deviceIcon = (type, active) => {
  const t = key(type);
  if (GATE_TYPES.includes(t)) return mdiGate;
  switch (t) {
    case T.WATER_LEVEL_CONTROLLER:
      return mdiWater;
    case T.LIGHT:
      return active ? mdiLightbulbVariant : mdiLightbulbVariantOutline;
    case T.SOCKET:
      return mdiPowerSocketUs;
    case T.FAN:
      return active ? mdiFan : mdiCeilingFan;
    case T.SWITCH:
      return mdiPowerStandby;
    case T.IRBM:
      return mdiRemoteTv;
    case R.AC:
      return mdiAirConditioner;
    case R.TV:
      return mdiMonitor;
    case R.STB:
      return mdiSetTopBox;
    case R.DVD:
      return mdiDiscPlayer;
    case R.STEREO:
      return mdiSpeaker;
    case R.PROJECTOR:
      return mdiProjector;
    case T.PIR_SENSOR:
      return active ? mdiMotionSensor : mdiMotionSensorOff;
    case T.GAS_SENSOR:
      return active ? mdiFireAlert : mdiFire;
    case T.SMOKE_DETECTOR:
      return active ? mdiSmokeDetector : mdiSmokeDetectorOff;
    case T.DW_SENSOR:
      return active ? mdiLock : mdiLockOpen;
    case T.DOOR_BELL:
      return active ? mdiDoorbellVideo : mdiDoorbell;
    case T.MOTOR:
      return mdiPump;
    case T.RGB_LIGHT:
      return mdiLedStripVariant;
    case T.CURTAIN_MODULE:
      return mdiCurtains;
    case T.DIMMER_MODULE:
      return mdiLightbulbVariant;
    case T.SMART_LOCK:
      return mdiDoorClosedLock;
    case T.ROBOT_VACUUM_CLEANER:
      return mdiRobotVacuum;
    case T.SECURITY_ALARM:
      return mdiShieldHome;
    case T.ENERGY_METER:
      return mdiLightningBolt;
    // EZVIZ cameras (app/screens/cam/CamDevice.js shows a video camera)
    case "CAMERA":
      return mdiCctv;
    default:
      return mdiPower;
  }
};
